/**
 * Audio Player Component
 * Fetches audio from authenticated endpoint and creates a playable blob URL
 *
 * Handles both:
 * - Authenticated asset URLs (/api/assets/:assetId)
 * - Direct URLs (data URIs, external URLs)
 */

'use client';
import { useEffect, useState } from 'react';
import { GetSeriousApiClient } from '@/lib/api';

interface AudioPlayerProps {
  audioUrl: string;
  apiClient: GetSeriousApiClient;
  className?: string;
  onPlay?: (event: React.SyntheticEvent<HTMLAudioElement>) => void;
  onPause?: (event: React.SyntheticEvent<HTMLAudioElement>) => void;
  onEnded?: (event: React.SyntheticEvent<HTMLAudioElement>) => void;
  onTimeUpdate?: (event: React.SyntheticEvent<HTMLAudioElement>) => void;
  audioRef?: React.RefObject<HTMLAudioElement>;
}

export function AudioPlayer({
  audioUrl,
  apiClient,
  className = 'mb-4 w-full',
  onPlay,
  onPause,
  onEnded,
  onTimeUpdate,
  audioRef
}: AudioPlayerProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let currentBlobUrl: string | null = null;

    // If audioUrl is already a data URI or external URL, use it directly
    if (!audioUrl.startsWith('/api/assets/')) {
      setBlobUrl(audioUrl);
      setLoading(false);
      return;
    }

    // Fetch audio with authentication for asset URLs
    const fetchAudio = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = apiClient.getAuthToken();
        if (!token) {
          throw new Error('No authentication token available');
        }

        const response = await fetch(audioUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load audio: ${response.statusText}`);
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        currentBlobUrl = url;
        setBlobUrl(url);
      } catch (err: any) {
        console.error('Error loading audio:', err);
        setError(err.message || 'Failed to load audio');
      } finally {
        setLoading(false);
      }
    };

    fetchAudio();

    // Cleanup blob URL on unmount or when audioUrl changes
    return () => {
      if (currentBlobUrl && currentBlobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(currentBlobUrl);
      }
    };
  }, [audioUrl, apiClient]);

  if (loading) {
    return <div className={`text-sm text-gray-400 ${className}`}>Loading audio...</div>;
  }

  if (error) {
    return <div className={`text-sm text-red-400 ${className}`}>Error: {error}</div>;
  }

  if (!blobUrl) {
    return null;
  }

  return (
    <audio
      ref={audioRef}
      controls
      src={blobUrl}
      className={className}
      onPlay={onPlay}
      onPause={onPause}
      onEnded={onEnded}
      onTimeUpdate={onTimeUpdate}
    ></audio>
  );
}
